import React, { useState } from "react";
import api, { formatApiError } from "@/api";
import { Sparkles, X } from "lucide-react";

function renderMarkdown(text) {
  // Minimal markdown: **bold**, *italic*, ## headings, - bullets, *disclaimer*
  const lines = (text || "").split("\n");
  return lines.map((line, i) => {
    if (/^\s*##\s+/.test(line)) {
      return (
        <h4 key={i} className="font-serif text-lg mt-4 mb-1" style={{ color: "var(--gain)" }}>
          {line.replace(/^\s*##\s+/, "")}
        </h4>
      );
    }
    if (/^\s*-\s+/.test(line)) {
      const content = line.replace(/^\s*-\s+/, "");
      return (
        <li key={i} className="ml-4 mb-1 list-disc text-sm"
            dangerouslySetInnerHTML={{ __html: content.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>") }} />
      );
    }
    if (!line.trim()) return <div key={i} className="h-2" />;
    return (
      <p key={i} className="text-sm mb-2 leading-relaxed"
         dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/\*(.+?)\*/g, "<em style='opacity:0.7'>$1</em>") }} />
    );
  });
}

export default function BriefingButton() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [briefing, setBriefing] = useState("");
  const [error, setError] = useState("");

  const generate = async () => {
    setLoading(true); setError(""); setBriefing("");
    try {
      const { data } = await api.post("/ai/briefing");
      setBriefing(data.briefing || "");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    if (!briefing && !loading) generate();
  };

  return (
    <>
      <button
        data-testid="ai-briefing-button"
        className="btn-secondary inline-flex items-center gap-2"
        onClick={handleOpen}
        title="AI-generated portfolio briefing"
      >
        <Sparkles size={12} /> Briefing
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(4px)" }}
          onClick={() => setOpen(false)}
          data-testid="ai-briefing-modal-backdrop"
        >
          <div
            className="card w-full max-w-2xl max-h-[90vh] overflow-y-auto fade-up"
            onClick={(e) => e.stopPropagation()}
            data-testid="ai-briefing-modal"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="label-xs mb-1 flex items-center gap-2">
                  <Sparkles size={11} style={{ color: "var(--gain)" }} /> AI briefing
                </p>
                <h3 className="font-serif text-2xl">Your portfolio in plain English</h3>
              </div>
              <button className="btn-ghost" onClick={() => setOpen(false)} data-testid="close-briefing-button">
                <X size={18} />
              </button>
            </div>

            {loading && (
              <div className="text-xs py-8" style={{ color: "var(--text-secondary)" }}>
                Generating your briefing<span className="blink">█</span>
              </div>
            )}
            {error && (
              <div className="text-xs px-3 py-2 mb-3"
                style={{ background: "var(--loss-bg)", color: "var(--loss)", border: "1px solid var(--loss)" }}>
                {error}
                <div className="text-[10px] mt-1 opacity-80">
                  If this says "budget exceeded", please top up your Emergent Universal Key balance.
                </div>
              </div>
            )}
            {briefing && (
              <div data-testid="ai-briefing-content" className="space-y-1">
                {renderMarkdown(briefing)}
              </div>
            )}

            {briefing && (
              <div className="flex gap-2 mt-6">
                <button
                  data-testid="regenerate-briefing-button"
                  className="btn-secondary"
                  onClick={generate}
                  disabled={loading}
                >
                  {loading ? "Regenerating..." : "Regenerate"}
                </button>
                <button className="btn-primary flex-1" onClick={() => setOpen(false)}>
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
